import { computed, ref, useStore } from "@nuxtjs/composition-api";

import { useCreatorShopPostsStore, useCreatorShopSearchStore } from "@/store";
import { useCreatorProductStore } from "@/store/creator-products";
import { getExperimentsStore } from "@/store/experiments";
import { SEARCH_DEFAULT_PAGE_SIZE } from "@/utils/constants";
import { FEATURE_FLAG_ACTIVE, FLAG_CREATOR_SHOP_SEARCH } from "@/utils/experiments/constants";

export const useCreatorShopSearchProducts = (profileId: string) => {
  if (!profileId) {
    throw new Error("useCreatorShopSearchProducts() requires a profileId be supplied.");
  }

  const store = useStore();
  const creatorShopSearchStore = useCreatorShopSearchStore();
  const experimentsStore = getExperimentsStore(store);
  const creatorProductStore = useCreatorProductStore();

  const searchPage = ref(1);
  const searchLoading = ref(false);
  const searchPaginating = ref(false);
  const searchError = ref(false);

  const isCreatorShopSearchEnabled = computed(
    () => experimentsStore.flag(FLAG_CREATOR_SHOP_SEARCH)?.value === FEATURE_FLAG_ACTIVE,
  );

  const showLoading = computed(() => searchLoading.value);
  const showError = computed(() => searchError.value);

  const legacyProductSearchFeed = computed(() => creatorProductStore.recentProductsSearchFeed);
  const productSearchFeed = computed(() => creatorShopSearchStore.productsSearchFeedMap);

  const hasMoreProducts = (query = "") => {
    const productsFeed = creatorShopSearchStore.productsSearchFeedMap[query];
    if (!productsFeed) {
      return false;
    }
    const productsTotal = productsFeed.total;
    const productsTotalPages = Math.ceil(productsTotal / SEARCH_DEFAULT_PAGE_SIZE);
    const productsComplete = productsFeed.complete;
    const productsCurrentPage = searchPage.value;
    if (!productsComplete) {
      return true;
    }
    return productsCurrentPage < productsTotalPages;
  };

  const onSearchMoreProducts = async (query: string) => {
    if (searchLoading.value || searchPaginating.value || !query?.trim()) {
      return;
    }
    try {
      searchPaginating.value = true; // set paginating state to true
      if (isCreatorShopSearchEnabled.value) {
        searchPage.value += 1; // increment the page for the next fetch
        if (hasMoreProducts(query)) {
          await creatorShopSearchStore.fetchMoreProductSearchResults(profileId, {
            query,
            page: searchPage.value,
            limit: SEARCH_DEFAULT_PAGE_SIZE,
            aggregated: false,
          });
        }
      } else {
        await creatorProductStore.searchCreatorRecentProducts(profileId, query);
      }
    } catch {
      searchError.value = true; // set error state if the fetch fails
    }
    searchPaginating.value = false; // reset paginating state
  };

  const onSearchProducts = async (query: string) => {
    if (!query?.trim()) {
      return;
    }
    try {
      searchError.value = false;
      searchPage.value = 1; // reset the page to 1 for the new search term
      searchLoading.value = true; // set loading to true while we fetch
      if (isCreatorShopSearchEnabled.value) {
        await creatorShopSearchStore.fetchProductSearchResults(profileId, {
          query,
          page: searchPage.value,
          limit: SEARCH_DEFAULT_PAGE_SIZE,
          aggregated: false,
        });
      } else {
        await creatorProductStore.searchCreatorRecentProducts(profileId, query);
      }
    } catch {
      searchError.value = true; // set error state if the fetch fails
    }
    searchLoading.value = false; // reset loading state
  };

  const resetPagination = () => {
    searchPage.value = 1;
    searchLoading.value = false;
    searchPaginating.value = false;
    searchError.value = false;
  };

  const resetSearch = () => {
    creatorProductStore.recentProductsSearchFeed = {};
    creatorShopSearchStore.resetSearch();
  };

  return {
    legacyProductSearchFeed,
    productSearchFeed,
    showLoading,
    showError,
    onSearchProducts,
    onSearchMoreProducts,
    resetPagination,
    resetSearch,
  };
};

export const useCreatorShopSearchPosts = (profileId: string) => {
  if (!profileId) {
    throw new Error("useCreatorShopSearchPosts() requires a profileId be supplied.");
  }

  const store = useStore();
  const creatorShopSearchStore = useCreatorShopSearchStore();
  const experimentsStore = getExperimentsStore(store);
  const creatorShopPostsStore = useCreatorShopPostsStore();

  const searchPage = ref(1);
  const searchLoading = ref(false);
  const searchPaginating = ref(false);
  const searchError = ref(false);

  const isCreatorShopSearchEnabled = computed(
    () => experimentsStore.flag(FLAG_CREATOR_SHOP_SEARCH)?.value === FEATURE_FLAG_ACTIVE,
  );

  const showLoading = computed(() => searchLoading.value);
  const showError = computed(() => searchError.value);

  const legacyLtksSearchFeed = computed(() => creatorShopPostsStore.postSearchFeedMap);
  const ltksSearchFeed = computed(() => creatorShopSearchStore.ltksSearchFeedMap);

  const hasMorePosts = (query = "") => {
    const postsFeed = creatorShopSearchStore.ltksSearchFeedMap[query];
    if (!postsFeed) {
      return false;
    }

    const ltksTotal = postsFeed.total;
    const ltksTotalPages = Math.ceil(ltksTotal / SEARCH_DEFAULT_PAGE_SIZE);
    const ltksComplete = postsFeed.complete;
    const ltksCurrentPage = searchPage.value;
    if (!ltksComplete) {
      return true;
    }
    return ltksCurrentPage < ltksTotalPages;
  };

  const onSearchMorePosts = async (query: string) => {
    if (searchLoading.value || searchPaginating.value || !query?.trim()) {
      return;
    }
    try {
      searchPaginating.value = true; // set paginating state to true
      if (isCreatorShopSearchEnabled.value) {
        searchPage.value += 1; // increment the page for the next fetch
        if (hasMorePosts(query)) {
          await creatorShopSearchStore.fetchMoreLtkSearchResults(profileId, {
            query,
            page: searchPage.value,
            limit: SEARCH_DEFAULT_PAGE_SIZE,
            aggregated: true,
            types: ["post", "product"],
          });
        }
      } else {
        await creatorShopPostsStore.fetchMoreBySearchTerm(profileId, query);
      }
    } catch {
      searchError.value = true; // set error state if the fetch fails
    }
    searchPaginating.value = false; // reset paginating state
  };

  const onSearchPosts = async (query: string) => {
    if (!query?.trim()) {
      return;
    }
    try {
      searchError.value = false;
      searchPage.value = 1; // reset the page to 1 for the new search term
      searchLoading.value = true; // set loading to true while we fetch
      if (query) {
        if (isCreatorShopSearchEnabled.value) {
          await creatorShopSearchStore.fetchLtkSearchResults(profileId, {
            query,
            page: searchPage.value,
            limit: SEARCH_DEFAULT_PAGE_SIZE,
            aggregated: true,
            types: ["post", "product"],
          });
        } else {
          await creatorShopPostsStore.fetchBySearchTerm(profileId, query);
        }
      }
    } catch {
      searchError.value = true; // set error state if the fetch fails
    }
    searchLoading.value = false; // reset loading state
  };

  const resetPagination = () => {
    searchPage.value = 1;
    searchLoading.value = false;
    searchPaginating.value = false;
    searchError.value = false;
  };

  const resetSearch = () => {
    creatorShopPostsStore.$reset();
    creatorShopSearchStore.resetSearch();
  };

  return {
    legacyLtksSearchFeed,
    ltksSearchFeed,
    showLoading,
    showError,
    onSearchPosts,
    onSearchMorePosts,
    resetPagination,
    resetSearch,
  };
};
